/**
 * उपस्थिति (QR) — the ID-card scanner (N-PAC, 4 Nov 2026: "आइडी कार्ड स्कैनर जोड़िए, और एक्सेस देने वाले ऑप्शन में
 * इसका भी एक्सेस रखिए"): pick the programme (today's rally, the candidate's tour day, or a meeting by name),
 * scan the worker's card — or type the mobile number — and the name, booth and running count come back.
 * Online only: every scan is one request, so the count is always the real one.
 */
import { useEffect, useMemo, useState } from "react";
import { apiErrorText } from "@core/messages";
import { istParts } from "../field/time";
import { useApp, useSession } from "../ui/context";
import { Banner, Button, Card, Field } from "../ui/components";
import { QrScanner } from "../ui/QrScanner";
import type { Rally } from "../field/api";

type EventKind = "rally" | "tour" | "meeting";
interface MarkReply {
  duplicate: boolean;
  total: number;
  person: { name: string; designation: string | null; role: string; booth: string | null; boothName: string | null; phone: string; at: string };
}
interface Entry {
  id: number;
  name: string;
  role: string;
  via: string;
  at: string;
  booth: string | null;
}

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export function AttendanceScreen() {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const today = istParts(Date.now()).date;
  const [kind, setKind] = useState<EventKind>("rally");
  const [rallies, setRallies] = useState<Rally[]>([]);
  const [rallyId, setRallyId] = useState("");
  const [meeting, setMeeting] = useState("");
  const [scan, setScan] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [needName, setNeedName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "warn" | "error" | "info"; text: string } | null>(null);
  const [last, setLast] = useState<MarkReply["person"] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);

  // today's and upcoming rallies, nearest first
  useEffect(() => {
    if (!assemblyId) return;
    app
      .online((api, aid) => api.campaign<{ items: Rally[] }>(aid, "rallies"))
      .then((r) => {
        const list = (r.items ?? []).filter((x) => x.status !== "cancelled").sort((a, b) => Math.abs(new Date(a.startsAt).getTime() - Date.now()) - Math.abs(new Date(b.startsAt).getTime() - Date.now()));
        setRallies(list);
        if (list[0] && !rallyId) setRallyId(String(list[0].id));
      })
      .catch(() => setRallies([]));
  }, [app, assemblyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const event = useMemo(() => {
    if (kind === "rally") return rallyId ? { kind: "rally" as const, key: rallyId } : null;
    if (kind === "tour") return { kind: "tour" as const, key: today };
    return meeting.trim().length >= 2 ? { kind: "meeting" as const, key: meeting.trim() } : null;
  }, [kind, rallyId, meeting, today]);

  // the event's count and list so far
  useEffect(() => {
    if (!event || !assemblyId) {
      setEntries([]);
      setTotal(null);
      return;
    }
    let alive = true;
    app
      .online((api, aid) => api.attendanceList(aid, event.kind, event.key))
      .then((r) => {
        if (!alive) return;
        setEntries(r.entries);
        setTotal(r.total);
      })
      .catch(() => {
        if (alive) setTotal(null);
      });
    return () => {
      alive = false;
    };
  }, [app, assemblyId, event?.kind, event?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = async (body: { code?: string; phone?: string; name?: string }) => {
    if (!event) return setMsg({ kind: "warn", text: t.atPickEvent });
    setBusy(true);
    setMsg(null);
    try {
      const r = await app.online((api, aid) => api.attendanceMark(aid, { event, ...body }));
      setLast(r.person);
      setTotal(r.total);
      setMsg(r.duplicate ? { kind: "info", text: t.atAlready(r.person.name) } : { kind: "ok", text: t.atMarked(r.person.name) });
      setNeedName(false);
      setPhone("");
      setName("");
      setEntries((list) => (r.duplicate ? list : [{ id: Date.now(), name: r.person.name, role: r.person.role, via: body.code ? "qr" : "manual", at: r.person.at, booth: r.person.booth }, ...list]));
      if (navigator.vibrate) navigator.vibrate(r.duplicate ? [60, 40, 60] : 80);
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === "not_registered") {
        setNeedName(true);
        setMsg({ kind: "warn", text: t.atNotRegistered });
      } else setMsg({ kind: "error", text: apiErrorText(e, lang) ?? t.err_generic });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" data-testid="attendance">
      <div className="big-title">🪪 {t.atTitle}</div>
      <p className="muted" style={{ margin: 0 }}>
        {t.atHelp}
      </p>

      <Card>
        <div className="label">{t.atEvent}</div>
        <div className="chips" role="radiogroup">
          {(["rally", "tour", "meeting"] as EventKind[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={`chip${kind === k ? " chip-on" : ""}`} onClick={() => setKind(k)} data-testid={`at-kind-${k}`}>
              {t.atKind(k)}
            </button>
          ))}
        </div>
        {kind === "rally" ? (
          rallies.length ? (
            <select className="input" value={rallyId} onChange={(e) => setRallyId(e.target.value)} data-testid="at-rally" style={{ marginTop: 8 }}>
              {rallies.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {new Date(r.startsAt).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" })} · {r.title} — {r.place}
                </option>
              ))}
            </select>
          ) : (
            <p className="muted" style={{ margin: "8px 0 0" }}>
              {t.atNoRallies}
            </p>
          )
        ) : kind === "tour" ? (
          <p className="muted" style={{ margin: "8px 0 0" }}>
            {t.atTourToday(today)}
          </p>
        ) : (
          <div style={{ marginTop: 8 }}>
            <Field label={t.atMeetingName} name="at-meeting" value={meeting} onChange={setMeeting} placeholder={t.atMeetingPh} maxLength={80} />
          </div>
        )}
        {total != null ? (
          <p className="at-total" data-testid="at-total">
            <b>{total}</b> {t.atSoFar}
          </p>
        ) : null}
      </Card>

      {msg ? (
        <Banner kind={msg.kind} testId="at-msg">
          {msg.text}
        </Banner>
      ) : null}
      {last ? (
        <Card testId="at-last">
          <div className="at-person">
            <b>{last.name}</b>
            <small>
              {[last.designation, last.booth ? `${t.boothNo} ${last.booth}` : null, last.boothName].filter(Boolean).join(" · ")}
            </small>
          </div>
        </Card>
      ) : null}

      <Card>
        <Button big onClick={() => setScan((s) => !s)} disabled={!event || busy} testId="at-scan">
          {scan ? t.vtScanClose : t.atScan}
        </Button>
        {scan ? (
          <div style={{ marginTop: 12 }}>
            <QrScanner
              t={t}
              onCode={(code) => {
                setScan(false);
                void mark({ code });
              }}
              onClose={() => setScan(false)}
            />
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <Field label={t.atPhone} name="at-phone" value={phone} onChange={(v) => setPhone(v.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="98765 43210" />
          {needName ? <Field label={t.atName} name="at-name" value={name} onChange={setName} maxLength={120} /> : null}
          <Button onClick={() => void mark({ phone, name: needName ? name.trim() || undefined : undefined })} disabled={!event || busy || phone.length !== 10 || (needName && !name.trim())} testId="at-mark">
            ✓ {t.atMark}
          </Button>
        </div>
      </Card>

      {entries.length ? (
        <Card testId="at-list">
          <div className="label">{t.atList}</div>
          <ol className="at-entries">
            {entries.slice(0, 50).map((e) => (
              <li key={e.id}>
                <span>
                  <b>{e.name}</b>
                  {e.booth ? <small> · {t.boothNo} {e.booth}</small> : null}
                </span>
                <small className="muted">
                  {e.via === "qr" ? "QR" : "📞"} {fmtTime(e.at)}
                </small>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}
    </div>
  );
}
